import { TableProps } from '@amzn/awsui-components-react';

/**
 * Defines Custom Table Preference used in Polaris Collection Preferences
 */
export interface Preference {
    custom?: string;
    pageSize?: number;
    visibleContent?: readonly string[] | undefined;
    wrapLines?: boolean;
}

/**
 * Defines the visible content config that is used in Polaris Collection Preference
 */
export interface CollectionPreferenceVisibleContentConfig {
    options: {
        label: string;
        options: { id: string; label: string; editable?: boolean; }[];
    }[];
    title: string;
}

/**
 * Defines the visible content option input data format
 */
export interface VisibleContentOption {
    id: string;
    label: string;
    editable?: boolean;
}

/**
 * Defines filter operators
 */
export declare type Operator = '<' | '<=' | '>' | '>=' | ':' | '!:' | '=' | '!=';

/**
 * Defines properties by which the data set can be filtered.
 */
export interface FilterProperty {
    key: string; // The identifier of this property, use column id
    groupValuesLabel: string; // Localized string to display for group label
    propertyLabel: string; // Label to be displayed on UI, use column header + 'Values'
    operators?: readonly Operator[];
    defaultOperator?: Operator;
    group?: string;
}

/**
 * Defines columns types
 */
export enum ColumnTypes {
    TEXT = 'text',
    NUMBER = 'number',
    BOOLEAN = 'boolean',
    CUSTOM = 'custom',
}

/**
 * Defines table column definitions
 */
export interface ColumnDefinition<I> extends TableProps.ColumnDefinition<I> {
    id: string; // Unique ID will be required to generate table preference
    text?: string; // column header label
    type: ColumnTypes;
    uom?: string;
    data?(item: I): number | string; // value to download, if not defined by id
}

/**
 * Defines properties required to add a custom property filtering group
 * example: properties: 'Tags', values: 'Tag values', group: 'tags'
 */
export interface CustomPropertyFilterGroup<T> {
    propertyDefinitions: ColumnDefinition<T>[];
    properties: string;
    values: string;
    group: string;
}

/**
 * Defines the operation options for the property filter
 */
export type Operation = 'and' | 'or';

/**
 * Defines one piece of a query
 */
export interface Token {
    value: string;
    propertyKey?: string;
    operator: Operator;
}

/**
 * Defines a query used in the property filter
 */
export interface Query {
    tokens: readonly Token[];
    operation: Operation;
}

/**
 * Defines an option for property filtering
 */
export interface PropertyFilteringOption {
    propertyKey: string;
    value: string;
}
